'use client';

import { useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { SectionHeader, Grad } from '@/components/ui/Section';

interface Product {
  id: string;
  name: string;
  category: string;
  description: string;
  concentration: string;
  applications: string[];
  benefits: string[];
  icon: string;
  image: string;
  imageAlt: string;
  /** Stable resource ID from src/lib/downloadResources.ts */
  tdsResourceId?: string;
}

interface ProductCategoriesProps {
  onDownloadTDS?: (resourceId: string, productName: string) => void;
}

const ProductCategories = ({ onDownloadTDS }: ProductCategoriesProps) => {
  const [selectedProduct, setSelectedProduct] = useState<string>('prod_lubricants');

  const products: Product[] = [
    {
      id: 'prod_lubricants',
      name: 'GraphLube™ Series',
      category: 'Lubricant Additives',
      description:
        'Advanced graphene nanoplatelets engineered for oil-based and synthetic lubricants',
      concentration: '0.005-1% recommended dosage',
      tdsResourceId: 'tds-graphlube',
      applications: [
        'Automotive engine oils',
        'Industrial hydraulic fluids',
        'Gear oils',
        'Metalworking fluids',
        'Grease formulations',
      ],

      benefits: [
        'Reduce friction coefficient by 20-40%',
        'Extend equipment service life',
        'Lower operating temperatures',
        'Decrease maintenance intervals',
        'Improve fuel efficiency',
      ],

      icon: 'CogIcon',
      image: '/assets/images/Graphene Solutions.jpeg',
      imageAlt:
        'Industrial lubricant oil with graphene additives showing reduced friction properties',
    },
    {
      id: 'prod_concrete',
      name: 'GraphCrete™ Series',
      category: 'Concrete Enhancement',
      description: 'Nano-reinforcement additives for high-performance concrete applications',
      concentration: '0.01-0.03% by cement weight',
      tdsResourceId: 'tds-graphcrete',
      applications: [
        'High-rise construction',
        'Bridge infrastructure',
        'Marine structures',
        'Precast elements',
        'Self-healing concrete',
      ],

      benefits: [
        'Increase compressive strength by 30-40%',
        'Enhance durability and crack resistance',
        'Reduce water permeability by 60%',
        'Improve freeze-thaw resistance',
        'Enable thinner structural elements',
      ],

      icon: 'BuildingOffice2Icon',
      image: '/assets/images/GraphCrete.png',
      imageAlt:
        'High-performance concrete mixture with graphene reinforcement for enhanced strength',
    },
    {
      id: 'prod_composites',
      name: 'GraphComp™ Series',
      category: 'Composite Materials',
      description: 'Graphene additives for polymer matrix composites and advanced materials',
      concentration: '0.1-5% by resin weight',
      tdsResourceId: 'tds-graphcomp',
      applications: [
        'Aerospace components',
        'Automotive body panels',
        'Wind turbine blades',
        'Sports equipment',
        'Electronics housing',
      ],

      benefits: [
        'Boost thermal conductivity by 20-400%',
        'Increase mechanical strength by 20-30%',
        'Reduce weight by 15-20%',
        'Add electrical conductivity',
        'Improve impact resistance',
      ],

      icon: 'RocketLaunchIcon',
      image: '/assets/images/GraphComp.png',
      imageAlt:
        'Advanced composite materials with graphene showing lightweight and high-strength properties',
    },
    {
      id: 'prod_rubber',
      name: 'GraphFlex™ Series',
      category: 'Rubber Applications',
      description: 'Specialty graphene for elastomer and rubber compound enhancement',
      concentration: '0.1-10% by rubber weight',
      tdsResourceId: 'tds-graphflex',
      applications: [
        'Tire manufacturing',
        'Industrial seals and gaskets',
        'Conveyor belts',
        'Vibration dampers',
        'Conductive rubber products',
      ],

      benefits: [
        'Enhance tensile strength by 20-50%',
        'Add electrical conductivity (10⁴-10⁶ S/m)',
        'Improve thermal stability',
        'Increase tear resistance',
        'Reduce rolling resistance (tires)',
      ],

      icon: 'BoltIcon',
      image: '/assets/images/GraphFlex.png',
      imageAlt:
        'Industrial rubber products enhanced with graphene for improved electrical conductivity',
    },
  ];

  const activeProduct = products.find((p) => p.id === selectedProduct) || products[0];
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  /** Left/Right arrows move selection and focus between product tabs. */
  const onTablistKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = products.findIndex((p) => p.id === selectedProduct);
    const next =
      e.key === 'ArrowRight'
        ? (i + 1) % products.length
        : (i - 1 + products.length) % products.length;
    setSelectedProduct(products[next].id);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="products" className="section relative overflow-hidden bg-ink text-white">
      {/* Layered background: navy base, one radial blue light behind the
          content, faint technical-dot detail pinned to the edges, and a darker
          centre so text always reads. Same ladder as the homepage bands. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(70rem 34rem at 50% 18%, rgba(14,165,233,0.10), transparent 62%),' +
            'radial-gradient(48rem 30rem at 50% 60%, rgba(6,12,24,0.85), transparent 75%),' +
            'linear-gradient(168deg, #0b1628 0%, #060c18 55%, #0a1526 100%)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-40 opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(rgba(56,189,248,0.9) 1px, transparent 1px)',
          backgroundSize: '17px 17px',
          maskImage: 'linear-gradient(90deg, black, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, black, transparent)',
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-40 opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(rgba(56,189,248,0.9) 1px, transparent 1px)',
          backgroundSize: '17px 17px',
          maskImage: 'linear-gradient(270deg, black, transparent)',
          WebkitMaskImage: 'linear-gradient(270deg, black, transparent)',
        }}
      />

      <div className="container-page relative">
        {/* Centred heading, no eyebrow — standardised presentation. */}
        <SectionHeader
          title={
            <>
              <Grad>Graphene Solutions</Grad> by Application
            </>
          }
          lead="Tailored formulations engineered for specific industry requirements"
        />

        {/* Product tabs */}
        <div
          role="tablist"
          aria-label="Product series"
          onKeyDown={onTablistKeyDown}
          className="scroll-x mt-10 flex gap-3 pb-2 lg:grid lg:grid-cols-4 lg:overflow-visible lg:pb-0"
        >
          {products.map((product, i) => {
            const isActive = product.id === selectedProduct;
            return (
              <button
                key={product.id}
                ref={(el) => {
                  tabRefs.current[i] = el;
                }}
                type="button"
                role="tab"
                id={`product-tab-${product.id}`}
                aria-selected={isActive}
                aria-controls="product-panel"
                tabIndex={isActive ? 0 : -1}
                onClick={() => setSelectedProduct(product.id)}
                className={`flex min-w-[230px] items-center gap-3.5 rounded-xl border px-5 py-4 text-left transition-[background-color,border-color,box-shadow,color] duration-base ease-out active:translate-y-px motion-reduce:active:transform-none lg:min-w-0 ${
                  isActive
                    ? 'bg-accent-rich border-sky-400/60 text-white shadow-[0_10px_30px_rgba(14,165,233,0.3)]'
                    : 'border-white/10 bg-[#0a1322]/80 text-slate-300 hover:border-accent/45 hover:bg-white/[0.06] hover:text-white'
                }`}
              >
                <Icon
                  name={product.icon as any}
                  size={24}
                  className={`shrink-0 ${isActive ? 'text-white' : 'text-accent'}`}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold leading-snug">{product.name}</span>
                  <span
                    className={`mt-0.5 block text-xs ${isActive ? 'text-white/80' : 'text-slate-400'}`}
                  >
                    {product.category}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Product panel — one product at a time; short fade + rise on switch. */}
        <div
          key={activeProduct.id}
          role="tabpanel"
          id="product-panel"
          aria-labelledby={`product-tab-${activeProduct.id}`}
          className="rise mt-9 grid min-w-0 items-start gap-9 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-12"
        >
          {/* Image panel with overlapping dosage card */}
          <div className="min-w-0 sm:pb-8">
            <div className="relative">
              <div className="relative overflow-hidden rounded-xl border border-accent/35 bg-[#0a1322] shadow-[0_24px_60px_rgba(2,8,23,0.55),inset_0_0_50px_rgba(14,165,233,0.05)]">
                <div className="relative aspect-[4/3]">
                  <AppImage
                    src={activeProduct.image}
                    alt={activeProduct.imageAlt}
                    fill
                    sizes="(max-width: 1024px) 100vw, 45vw"
                    className="h-full w-full object-cover"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-gradient-to-t from-[#0a1322]/55 via-transparent to-transparent"
                  />
                </div>
              </div>

              {/* Dosage card — overlaps lower-right on sm+, stacks on mobile. */}
              <div className="relative mt-4 w-fit max-w-full rounded-lg border border-accent/40 bg-[#0a1322]/95 px-5 py-3.5 shadow-[0_12px_30px_rgba(2,8,23,0.5)] sm:absolute sm:-bottom-8 sm:right-5 sm:mt-0">
                <div className="flex items-center gap-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent">
                    <Icon name="BeakerIcon" size={19} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs uppercase tracking-wider text-slate-400">
                      Dosage
                    </span>
                    <span className="t-value block text-[0.9375rem] font-bold text-sky-400 sm:text-base">
                      {activeProduct.concentration}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          <div className="min-w-0">
            <div className="flex items-center gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent">
                <Icon name={activeProduct.icon as any} size={24} />
              </span>
              <span className="min-w-0">
                <h3 className="t-h2 text-white">{activeProduct.name}</h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-accent">
                  {activeProduct.category}
                </p>
              </span>
            </div>

            <p className="t-body mt-5 text-slate-300">{activeProduct.description}</p>

            <div className="mt-8 grid gap-8 border-t border-white/10 pt-7 md:grid-cols-2">
              <div className="min-w-0">
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-accent">
                  <Icon name="WrenchScrewdriverIcon" size={16} />
                  Key Applications
                </h4>
                <ul className="mt-4 space-y-3">
                  {activeProduct.applications.map((app, index) => (
                    <li
                      key={`app_${index}`}
                      className="flex items-start gap-2.5 text-sm text-slate-200"
                    >
                      <Icon
                        name="CheckCircleIcon"
                        size={18}
                        className="mt-0.5 shrink-0 text-accent"
                      />
                      <span className="min-w-0">{app}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="min-w-0">
                <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-accent">
                  <Icon name="ArrowTrendingUpIcon" size={16} />
                  Performance Benefits
                </h4>
                <ul className="mt-4 space-y-3">
                  {activeProduct.benefits.map((benefit, index) => (
                    <li
                      key={`benefit_${index}`}
                      className="flex items-start gap-2.5 text-sm text-slate-300"
                    >
                      <Icon
                        name="ArrowRightIcon"
                        size={17}
                        className="mt-0.5 shrink-0 text-accent"
                      />
                      <span className="min-w-0">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-9 flex flex-col gap-3 border-t border-white/10 pt-7 sm:flex-row">
              <Link
                href="/contact"
                className="group inline-flex min-h-[52px] flex-1 items-center justify-center gap-2.5 rounded-md bg-accent px-6 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] text-white shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-[background-color,box-shadow,transform] duration-fast ease-out hover:bg-sky-400 hover:shadow-[0_5px_20px_rgba(14,165,233,0.45)] active:translate-y-px motion-reduce:active:transform-none"
              >
                Contact Us
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                  className="transition-transform duration-fast ease-out group-hover:translate-x-[3px] motion-reduce:transform-none"
                >
                  <path
                    d="M3 10h13M11 4.5 16.5 10 11 15.5"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Link>
              <button
                type="button"
                onClick={() =>
                  onDownloadTDS?.(
                    activeProduct.tdsResourceId || 'product-catalog',
                    `${activeProduct.name} Technical Data Sheet`
                  )
                }
                className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2.5 rounded-md border border-accent/35 bg-white/[0.03] px-6 text-[0.8125rem] font-semibold uppercase tracking-[0.12em] text-white transition-[border-color,background-color] duration-fast ease-out hover:border-accent/60 hover:bg-accent/10 active:translate-y-px motion-reduce:active:transform-none"
              >
                Download TDS
                <Icon name="ArrowDownTrayIcon" size={17} className="shrink-0" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
